// Angular
import { ChangeDetectorRef, Compiler, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Observable, Subject } from 'rxjs';
import { filter, finalize, takeUntil, tap } from 'rxjs/operators';
// Translate
import { TranslateService } from '@ngx-translate/core';
// Store
import { Store } from '@ngrx/store';
import { AppState } from 'app/core/reducers';
// Auth
import { AuthNoticeService, AuthService, Login } from 'app/core/auth';
import { CookiesService } from 'app/core/auth/_services/cookies.service';
import { UserDataModel } from 'app/core/ana/authorization/models/user-data-model';
import { HttpErrorResponse } from '@angular/common/http';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BAMessageRedirectService } from 'app/core/o2c/inquiries/_services/ba-message-redirect.service';
import { environment } from 'environments/environment';

/**
 * ! Just example => Should be removed in development
 */
const DEMO_PARAMS = {
    /*USERNAME: 'Recheverri',
    PASSWORD: 'bananas'*/
    USERNAME: '',
    PASSWORD: ''
};

@Component({
  standalone: false,
    selector: 'kt-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    styles: [`
	.mat-icon-rtl-mirror {
		cursor: default;
	  }
	`]
})
export class LoginComponent implements OnInit, OnDestroy {
	isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    // Public params
    loginForm: FormGroup;
    loading = false;
    isLoggedIn$: Observable<boolean>;
    errors: any = [];
    hide = true;

    private unsubscribe = new Subject<void>();

    private returnUrl: any;

    /**
     * Store the token
     */
    private token: string;
	updateCheckText: string;

    // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

    /**
     * Component constructor
     *
     * @param router: Router
     * @param auth: AuthService
     * @param authNoticeService: AuthNoticeService
     * @param translate: TranslateService
     * @param store: Store<AppState>
     * @param fb: FormBuilder
     * @param cdr
     * @param route
     */
    constructor(
        private router: Router,
        private auth: AuthService,
        private authNoticeService: AuthNoticeService,
        private translate: TranslateService,
        private store: Store<AppState>,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
        private cookiesService: CookiesService,
		private updates: SwUpdate,
		private _compiler: Compiler,
        private baMessageRedirect: BAMessageRedirectService
    ) {
    }

    /**
     * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
     */

    /**
     * On init
     */
    ngOnInit(): void {
        this.initLoginForm();

        // redirect back to the returnUrl before login
        this.route.queryParams.subscribe(params => {
            this.returnUrl = params.returnUrl || '/';
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.authNoticeService.setNotice(null);
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.loading = false;
    }

    /**
     * Form initalization
     * Default params, validators
     */
    initLoginForm() {
        // demo message to show
        if (!this.authNoticeService.onNoticeChanged$.getValue()) {
            // const initialNotice = `Use account
            // <strong>${DEMO_PARAMS.USERNAME}</strong> and password
            // <strong>${DEMO_PARAMS.PASSWORD}</strong> to continue.`;
            // this.authNoticeService.setNotice(initialNotice, 'info');
        }

        this.loginForm = this.fb.group({
            /**
             * Send settings to the username field of the login component form
             * @author Castor Transformación Digital - Juan G. Pavas
             * @since 2019-11-14
             */
            username: [DEMO_PARAMS.USERNAME, Validators.compose([
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(100)
            ])
            ],
            password: [DEMO_PARAMS.PASSWORD, Validators.compose([
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(100)
            ])
            ],
            rememberMe: [false]
        });

        // Read the cookie setted to rememberMe option
		if(localStorage.getItem('rememberMe') == "true")
		{
			this.loginForm.controls.username.setValue( localStorage.getItem('username'));
            this.loginForm.controls.password.setValue(localStorage.getItem('userpassword'));
            this.loginForm.controls.rememberMe.setValue(true);
		}
        /*const cookie = this.cookiesService.getCookie('_remCred_', true);
		let data : UserDataModel = JSON.parse(cookie);
        if (cookie !== '' && data.rememberMe) {
            const authData: UserDataModel = JSON.parse(cookie);
            this.loginForm.controls.username.setValue(authData.username);
            this.loginForm.controls.password.setValue(authData.password);
            this.loginForm.controls.rememberMe.setValue(true);
        }*/
    }

    /**
     * Form Submit
     */
    submit() {

		this.checkUpdates();
		this._compiler.clearCache();
		localStorage.clear();


        const controls = this.loginForm.controls;
        /** check form */
        if (this.loginForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );
            return;
        }

        this.loading = true;

        const authData = {
            /**
             * Set the user's username for authentication
             * @author Castor Transformación Digital - Juan G. Pavas
             * @since 2019-11-14
             */
            username: controls.username.value,
            password: controls.password.value
        };

        /**
         * Call the login method that authenticates an user by username and password
         * @author Castor Transformación Digital - Juan G. Pavas
         * @since 2019-11-14
         */
        let succesfullLogin = false;
        this.auth
            .login(authData.username, authData.password)
            .pipe(
                tap(user => {
                    if (typeof user === 'object') {
                        if (user.isPasswordExpired) {
                            this.router.navigateByUrl(`/auth/change-password?user=${user.username}`);
                            return;
                        }
                        succesfullLogin = true;
                        this.auth.setUser(user);
                        // Set the cookie if not exist to remember the user and pass
                        if (controls.rememberMe.value) {
                            user.rememberMe = true;
							localStorage.setItem('rememberMe',"true");
                        }
                        const userString: string = JSON.stringify(user);
                        this.cookiesService.setCookie('_remCred_', userString, 60, true);
                        this.token = user.accessToken;
                        if (user.duoAuthenticationUrl) {
                            localStorage.setItem(environment.authTokenKey, this.token);
                            window.location.href = user.duoAuthenticationUrl.toString();
                        } else {
                            this.baMessageRedirect.redirectToMessageSearchIfNeeded(this.returnUrl).subscribe({
                                next: () => {
                                    this.loading = false;
                                    this.cdr.markForCheck();
                                },
                                error: () => {
                                    this.router.navigateByUrl(this.returnUrl || '/');
                                    this.loading = false;
                                    this.cdr.markForCheck();
                                }
                            });
                        }
                    } else {
                        this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
                        // this.authNoticeService.setNotice(user, 'danger');
                    }
                },
                    (error: HttpErrorResponse) => {
                        // console.log(error);
                        //this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
                        if(error.error.errors.length > 0)
						{
							this.authNoticeService.setNotice(error.error.errors[0], 'danger');
						}
						else
						{
							this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');

						}
                    }
                ),
                takeUntil(this.unsubscribe),
                finalize(() => {
                    if (!succesfullLogin) {
                        this.loading = false;
                        this.cdr.markForCheck();
                    }
                    if (succesfullLogin) {
                        this.store.dispatch(new Login({ authToken: this.token }));
                    }
                })
            )
            .subscribe();
    }

	/**
	 * Force it to reload the page from the server whenever a new app version is available.
	 */
	 private checkUpdates() {
        this.updates
            .checkForUpdate()
            .then(() => this.updateCheckText = 'resolved')
            .catch(err => this.updateCheckText = `rejected: ${err.message}`);
        this.updates.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe(() => {
                this.updates.activateUpdate().then(() => document.location.reload());
            });
    }

    /**
     * Checking control validation
     *
     * @param controlName: string => Equals to formControlName
     * @param validationType: string => Equals to valitors name
     */
    isControlHasError(controlName: string, validationType: string): boolean {
        const control = this.loginForm.controls[controlName];
        if (!control) {
            return false;
        }

        const result = control.hasError(validationType) && (control.dirty || control.touched);
        return result;
    }
}
